import { useScrollReveal } from '../../../../hooks/useScrollReveal';

const features = [
  {
    icon: 'ri-flight-land-line',
    title: 'Real-Time Flight Tracking',
    desc: 'We monitor your flight 24/7. If it\'s early, late, or diverted, we adjust your pickup time automatically. You never have to worry about calling us — we\'re already watching.',
  },
  {
    icon: 'ri-time-fill',
    title: 'Driver Always Arrives Early',
    desc: 'Your chauffeur arrives 15 minutes ahead of schedule. We track traffic, plan routes, and leave buffer time so you never have to rush to make your flight.',
  },
  {
    icon: 'ri-luggage-cart-line',
    title: 'Professional Luggage Assistance',
    desc: 'Your driver handles all luggage loading and unloading with care. No heavy lifting, no stress — just smooth, professional service from curbside to vehicle.',
  },
  {
    icon: 'ri-map-pin-2-fill',
    title: 'All 3 Charleston Airports',
    desc: 'We serve Charleston International (CHS), Charleston Executive Airport, and Mt. Pleasant Regional Airport — with the same on-time, luxury service at every terminal.',
  },
  {
    icon: 'ri-car-fill',
    title: 'Door-to-Door Transfers',
    desc: 'From your home, hotel, or office directly to the terminal — or straight from baggage claim to your destination. No stops, no detours, no shared rides.',
  },
  {
    icon: 'ri-calendar-check-fill',
    title: 'Easy Advance Booking',
    desc: 'Book your airport ride days, weeks, or months ahead. Receive instant confirmation with clear pricing, and update your booking anytime as plans change.',
  },
];

export default function AirportServiceBreakdown() {
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="section-reveal">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            {/* Text */}
            <div>
              <span className="text-gold text-xs tracking-widest uppercase font-semibold">Why Choose Us for Airport Transfers</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-neutral-900 mt-3 mb-6 leading-tight">
                Airport Transportation<br />
                <span className="text-gold">Done Right</span>
              </h2>
              <p className="text-neutral-600 leading-relaxed text-sm mb-6">
                We understand that airport transportation isn&apos;t just about getting from A to B — it&apos;s about arriving relaxed, on time, and stress-free. Every detail is managed so you can focus on your journey.
              </p>
              <p className="text-neutral-500 leading-relaxed text-sm">
                Serving <strong>Charleston International Airport (CHS)</strong>, <strong>Charleston Executive Airport</strong>, and <strong>Mt. Pleasant Regional Airport</strong> — with real-time flight monitoring, early arrivals, and professional luggage handling included on every booking.
              </p>
              <div className="mt-8 flex gap-4">
                <a href="#contact" className="bg-neutral-900 hover:bg-gold transition-colors text-white hover:text-black font-bold px-8 py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer">
                  Book Airport Transfer
                </a>
                <a href="tel:+18436422440" className="flex items-center gap-2 border-2 border-neutral-200 hover:border-gold text-neutral-700 hover:text-gold transition-colors font-semibold px-8 py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-sm"></i></div>
                  Call Now
                </a>
              </div>
            </div>
            {/* Image */}
            <div className="relative">
              <div className="w-full h-[400px] overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/1197a31b515904566eef7854e583c23f.jpg"
                  alt="Professional airport transportation service Charleston SC"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-gold/30 pointer-events-none"></div>
            </div>
          </div>

          {/* Feature grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feat) => (
              <div key={feat.title} className="group p-6 border border-neutral-100 hover:border-gold/30 hover:bg-neutral-50 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center bg-gold/10 group-hover:bg-gold/20 transition-colors mb-4">
                  <i className={`${feat.icon} text-gold text-xl`}></i>
                </div>
                <h4 className="font-semibold text-neutral-900 text-sm mb-2 group-hover:text-gold transition-colors">{feat.title}</h4>
                <p className="text-neutral-500 text-xs leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
